import type { IAiModel } from 'state/aiModels'
import { buildActionDefinitions, isVisibleActionDefinition } from 'state/actionDefinitions'
import type { ActionDefinition } from 'state/actionDefinitions'

export interface ActionCommandAutocompleteItem {
    id: string
    tag: string
    label: string
    type: 'model' | 'tool'
    actionDefinitionId?: string
    aiModelId?: string
    toolKey?: string
    humanName?: string
    displayName?: string | null
    descriptionShort?: string
    supportsPromptInput?: boolean
}

const actionSupportsPromptInput = (action: ActionDefinition): boolean => {
    if (Array.isArray(action.supportedInputs)) {
        return action.supportedInputs.includes('text')
    }

    if (action.kind === 'model') return true

    return action.promptRequired === true
}

function addUniqueCommand(
    map: Map<string, ActionCommandAutocompleteItem>,
    tag: string,
    type: ActionCommandAutocompleteItem['type'],
    options: {
        itemId?: string
        actionDefinitionId?: string
        aiModelId?: string
        toolKey?: string
        humanName?: string
        displayName?: string | null
        descriptionShort?: string
        supportsPromptInput?: boolean
    } = {},
) {
    const normalizedTag = (tag || '').trim().toLowerCase()
    if (!normalizedTag) return
    if (map.has(normalizedTag)) return

    map.set(normalizedTag, {
        id: options.itemId || options.actionDefinitionId || normalizedTag,
        tag: normalizedTag,
        label: `!${normalizedTag}`,
        type,
        actionDefinitionId: options.actionDefinitionId,
        aiModelId: options.aiModelId,
        toolKey: options.toolKey,
        humanName: options.humanName,
        displayName: options.displayName,
        descriptionShort: options.descriptionShort,
        supportsPromptInput: options.supportsPromptInput,
    })
}

export function buildActionCommandAutocompleteItemsFromDefinitions(
    actionDefinitions: ActionDefinition[] = [],
): ActionCommandAutocompleteItem[] {
    const uniqueItems = new Map<string, ActionCommandAutocompleteItem>()
    const visibleActions = actionDefinitions.filter(isVisibleActionDefinition)

    visibleActions.forEach(action => {
        addUniqueCommand(uniqueItems, action.commandTag, action.kind, {
            actionDefinitionId: action.id,
            aiModelId:
                action.kind === 'model'
                    ? action.aiModel?.id || action.defaultAiModelId || action.id
                    : undefined,
            toolKey: action.kind === 'tool' ? action.toolKey || action.tool?.key : undefined,
            humanName: action.humanName,
            displayName: action.displayName,
            descriptionShort: action.descriptionShort || action.description || undefined,
            supportsPromptInput: actionSupportsPromptInput(action),
        })
    })

    return Array.from(uniqueItems.values()).sort((a, b) => {
        if (a.type !== b.type) return a.type === 'model' ? -1 : 1
        return a.tag.localeCompare(b.tag)
    })
}

export function buildActionCommandAutocompleteItems(
    aiModels: IAiModel[] = [],
    actionDefinitions: ActionDefinition[] = [],
): ActionCommandAutocompleteItem[] {
    return buildActionCommandAutocompleteItemsFromDefinitions(
        actionDefinitions.length > 0 ? actionDefinitions : buildActionDefinitions(aiModels),
    )
}
